import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { Layout } from "../components/Layout";
import { SearchBar } from "../components/SearchBar";
import { StatusBadge } from "../components/StatusBadge";
import { StampButton } from "../components/StampButton";
import { searchCompanies } from "../lib/api";
import { formatSiren, titleCase } from "../lib/format";
import { NAF_SECTIONS } from "../lib/naf";
import { useHead } from "../lib/useHead";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  MapPin, Building2, AlertTriangle, SearchX, Loader2, ChevronRight,
  SlidersHorizontal, X, ChevronLeft,
} from "lucide-react";

const ResultRow = ({ r, index }) => {
  const navigate = useNavigate();
  const nom = titleCase(r.nom_complet || r.nom_raison_sociale || "Entreprise");
  const ville = r.ville ? titleCase(r.ville) : null;

  return (
    <div
      data-testid={`result-row-${r.siren}`}
      style={{ animationDelay: `${Math.min(index, 8) * 40}ms` }}
      className="animate-fade-up group flex items-center gap-3 border-b border-border bg-[hsl(var(--surface))] px-5 py-4 transition-colors hover:bg-[hsl(var(--brand-muted))]/30"
    >
      <button
        onClick={() => navigate(`/entreprise/${r.siren}`)}
        className="flex min-w-0 flex-1 items-center gap-4 text-left"
      >
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-border bg-[hsl(var(--background))] text-[hsl(var(--fg-muted))]">
          <Building2 className="h-5 w-5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="truncate font-display text-base font-semibold text-[hsl(var(--fg-primary))]">
              {nom}
            </span>
            {r.etat_administratif && (
              <StatusBadge etat={r.etat_administratif} libelle={r.etat_libelle} testId={`result-status-${r.siren}`} />
            )}
          </span>
          <span className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-base text-[hsl(var(--fg-secondary))]">
            <span className="font-mono text-sm text-[hsl(var(--fg-muted))]">SIREN {formatSiren(r.siren)}</span>
            {ville && (
              <span className="inline-flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5 text-[hsl(var(--fg-muted))]" />
                {ville}{r.code_postal ? ` (${r.code_postal})` : ""}
              </span>
            )}
            {r.activite_libelle && <span className="truncate">{r.activite_libelle}</span>}
          </span>
        </span>
      </button>
      <StampButton coordonnees={r.coordonnees} siren={r.siren} sourcePage="liste-resultats" variant="row" />
      <button onClick={() => navigate(`/entreprise/${r.siren}`)} aria-label="Voir la fiche" className="shrink-0">
        <ChevronRight className="h-5 w-5 text-[hsl(var(--fg-muted))] transition-transform group-hover:translate-x-0.5 group-hover:text-[hsl(var(--brand))]" />
      </button>
    </div>
  );
};

const FilterBar = ({ params, setParams, q }) => {
  const dep = params.get("departement") || "";
  const section = params.get("section") || "";
  const etat = params.get("etat") || "";
  const active = [dep, section, etat].filter(Boolean).length;

  const update = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    next.set("q", q);
    next.delete("page");
    setParams(next);
  };

  const reset = () => {
    const next = new URLSearchParams();
    next.set("q", q);
    setParams(next);
  };

  return (
    <div data-testid="filter-bar" className="mb-4 rounded-lg border border-border bg-[hsl(var(--surface))] p-4">
      <div className="mb-3 flex items-center justify-between">
        <span className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.15em] text-[hsl(var(--fg-muted))]">
          <SlidersHorizontal className="h-3.5 w-3.5" /> Filtres
          {active > 0 && <span className="rounded-full bg-[hsl(var(--brand))] px-1.5 text-[10px] text-white">{active}</span>}
        </span>
        {active > 0 && (
          <button data-testid="filter-reset" onClick={reset} className="inline-flex items-center gap-1 text-xs text-[hsl(var(--brand))] hover:underline">
            <X className="h-3 w-3" /> Réinitialiser
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <label className="mb-1 block text-sm font-medium text-[hsl(var(--fg-secondary))]">Département</label>
          <input
            data-testid="filter-departement"
            value={dep}
            onChange={(e) => update("departement", e.target.value.replace(/[^0-9AB]/gi, "").slice(0, 3))}
            placeholder="ex : 75, 2A, 974"
            inputMode="numeric"
            className="w-full rounded-md border border-border bg-[hsl(var(--surface))] px-3 py-2 text-sm focus:border-[hsl(var(--brand))] focus:outline-none"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-[hsl(var(--fg-secondary))]">Secteur d'activité</label>
          <Select value={section || "all"} onValueChange={(v) => update("section", v === "all" ? "" : v)}>
            <SelectTrigger data-testid="filter-section" className="w-full">
              <SelectValue placeholder="Tous les secteurs" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">Tous les secteurs</SelectItem>
              {NAF_SECTIONS.map((s) => (
                <SelectItem key={s.code} value={s.code}>{s.code} — {s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-[hsl(var(--fg-secondary))]">Statut</label>
          <Select value={etat || "all"} onValueChange={(v) => update("etat", v === "all" ? "" : v)}>
            <SelectTrigger data-testid="filter-etat" className="w-full">
              <SelectValue placeholder="Tous" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              <SelectItem value="A">Actif</SelectItem>
              <SelectItem value="C">Cessé</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
};

export default function Results() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const page = Math.max(1, parseInt(params.get("page") || "1", 10) || 1);
  const filters = {
    departement: params.get("departement") || "",
    section: params.get("section") || "",
    etat: params.get("etat") || "",
  };
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useHead({
    title: q ? `Recherche : ${q} — InfoSociété` : "Recherche — InfoSociété",
    description: `Résultats de recherche d'entreprises françaises pour « ${q} » sur InfoSociété.`,
    robots: "noindex,follow",
  });

  const isNameSearch = state.data?.detected_type === "name";

  const run = useCallback(async () => {
    setState({ loading: true, error: null, data: null });
    try {
      const data = await searchCompanies(q, page, filters);
      setState({ loading: false, error: null, data });
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      setState({ loading: false, error: { status, message: detail || "Une erreur est survenue lors de la recherche." }, data: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, page, filters.departement, filters.section, filters.etat]);

  useEffect(() => {
    if (q) run();
  }, [q, run]);

  const goToPage = (p) => {
    const next = new URLSearchParams(params);
    next.set("page", String(p));
    setParams(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Layout>
      <div className="mx-auto max-w-3xl px-5 py-8">
        <div className="mb-4">
          <SearchBar size="sm" initial={q} />
        </div>

        <FilterBar params={params} setParams={setParams} q={q} />

        {state.loading && (
          <div data-testid="results-loading" className="rounded-lg border border-border bg-[hsl(var(--surface))]">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-4 border-b border-border px-5 py-4 last:border-0">
                <div className="h-10 w-10 shrink-0 animate-pulse rounded-md bg-muted" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-1/2 animate-pulse rounded bg-muted" />
                  <div className="h-3 w-3/4 animate-pulse rounded bg-muted" />
                </div>
              </div>
            ))}
            <div className="flex items-center justify-center gap-2 py-4 text-sm text-[hsl(var(--fg-muted))]">
              <Loader2 className="h-4 w-4 animate-spin" /> Recherche en cours…
            </div>
          </div>
        )}

        {!state.loading && state.error && (
          <div data-testid="results-error" className="rounded-lg border border-[hsl(var(--warn))]/40 bg-[hsl(var(--warn))]/5 p-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-[hsl(var(--warn))]" />
              <div>
                <h2 className="font-display text-base font-semibold text-[hsl(var(--fg-primary))]">
                  {state.error.status === 400 ? "Saisie à vérifier" : state.error.status === 503 ? "Source indisponible" : "Recherche impossible"}
                </h2>
                <p className="mt-1 text-sm text-[hsl(var(--fg-secondary))]">{state.error.message}</p>
                {state.error.status === 503 && (
                  <button data-testid="retry-btn" onClick={run} className="mt-3 rounded-md border border-border bg-[hsl(var(--surface))] px-4 py-2 text-sm font-medium transition-colors hover:bg-muted">
                    Réessayer
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {!state.loading && !state.error && state.data && (
          <>
            <div className="mb-3 flex items-baseline justify-between">
              <h1 className="text-base text-[hsl(var(--fg-secondary))]" data-testid="results-count">
                {state.data.total_results > 0 ? (
                  <>
                    <span className="font-semibold text-[hsl(var(--fg-primary))]">{state.data.total_results.toLocaleString("fr-FR")}</span>{" "}
                    résultat{state.data.total_results > 1 ? "s" : ""} pour « {q} »
                  </>
                ) : null}
              </h1>
              {state.data.detected_type && state.data.total_results > 0 && (
                <span className="rounded-full border border-border bg-[hsl(var(--surface))] px-2.5 py-0.5 text-sm text-[hsl(var(--fg-secondary))]">
                  {{ siren: "SIREN", siret: "SIRET", name: "Nom" }[state.data.detected_type] || "Recherche"}
                </span>
              )}
            </div>

            {!isNameSearch && (filters.departement || filters.section || filters.etat) && (
              <p className="mb-3 text-sm text-[hsl(var(--fg-muted))]">
                Les filtres s'appliquent uniquement aux recherches par nom.
              </p>
            )}

            {state.data.results.length === 0 ? (
              <div data-testid="results-empty" className="rounded-lg border border-border bg-[hsl(var(--surface))] p-10 text-center">
                <SearchX className="mx-auto h-10 w-10 text-[hsl(var(--fg-muted))]" />
                <h2 className="mt-4 font-display text-lg font-semibold text-[hsl(var(--fg-primary))]">Aucun résultat</h2>
                <p className="mx-auto mt-2 max-w-md text-sm text-[hsl(var(--fg-secondary))]">
                  Aucune entreprise ne correspond à « {q} »{(filters.departement || filters.section || filters.etat) ? " avec ces filtres" : ""}.
                  Vérifiez l'orthographe ou essayez un numéro SIREN (9 chiffres) ou SIRET (14 chiffres).
                </p>
                <Link to="/" data-testid="empty-home-link" className="mt-4 inline-block text-sm font-medium text-[hsl(var(--brand))] hover:underline">
                  Nouvelle recherche
                </Link>
              </div>
            ) : (
              <>
                <div className="overflow-hidden rounded-lg border border-border">
                  {state.data.results.map((r, i) => (
                    <ResultRow key={r.siren} r={r} index={i} />
                  ))}
                </div>

                {state.data.total_pages > 1 && (
                  <div data-testid="pagination" className="mt-5 flex items-center justify-between">
                    <button
                      data-testid="page-prev"
                      disabled={page <= 1}
                      onClick={() => goToPage(page - 1)}
                      className="inline-flex items-center gap-1 rounded-md border border-border bg-[hsl(var(--surface))] px-4 py-2 text-sm font-medium transition-colors enabled:hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <ChevronLeft className="h-4 w-4" /> Précédent
                    </button>
                    <span className="text-sm text-[hsl(var(--fg-secondary))]">
                      Page {page} / {Math.min(state.data.total_pages, 1000)}
                    </span>
                    <button
                      data-testid="page-next"
                      disabled={page >= state.data.total_pages}
                      onClick={() => goToPage(page + 1)}
                      className="inline-flex items-center gap-1 rounded-md border border-border bg-[hsl(var(--surface))] px-4 py-2 text-sm font-medium transition-colors enabled:hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      Suivant <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
